import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RecurringSeriesOccurrence } from '@spike/new-booking-model';
import clsx from 'clsx';
import SidePanel from 'components/UI/SidePanel';
import Button from 'components/UI/V3/Button/Button';
import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { RECURRING_PANEL_WIDTH, useDockedPanelStyles } from '../panelDocking';
import { RecurringSeriesContent, RecurringSeriesTotals } from './RecurringSeriesContent';
import { useRecurringSeriesView } from './useRecurringSeriesView';
import styles from './RecurringSeriesView.module.scss';

interface RecurringSeriesViewPanelProps {
    recurringUuid: string;
    onClose: () => void;
    selectedBookingId?: number;
    onSelectBooking?: (occurrence: RecurringSeriesOccurrence) => void;
    refreshKey?: number;
    isUpdating?: boolean;
    zIndex?: number;
}

const PANEL_LEAVE_MS = 200;

export const RecurringSeriesViewPanel: FunctionComponent<RecurringSeriesViewPanelProps> = props => {
    const classes = useDockedPanelStyles();
    const [isLeaving, setIsLeaving] = useState(false);
    const leaveTimeout = useRef<number>();
    const series = useRecurringSeriesView(props.recurringUuid, props.refreshKey);

    useEffect(() => () => window.clearTimeout(leaveTimeout.current), []);

    const closeHandler = useCallback(() => {
        setIsLeaving(true);
        leaveTimeout.current = window.setTimeout(props.onClose, PANEL_LEAVE_MS);
    }, [props.onClose]);

    const content = (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <h2 className={styles.title}>Recurring Series</h2>
                <button
                    type="button"
                    className={styles.collapseButton}
                    onClick={closeHandler}
                >
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
                <RecurringSeriesTotals series={series} />
            </div>

            <div className={styles.body}>
                <RecurringSeriesContent
                    series={series}
                    isUpdating={props.isUpdating}
                    selectedBookingId={props.selectedBookingId}
                    onSelectBooking={props.onSelectBooking}
                />
            </div>

            <div className={styles.footer}>
                <Button
                    variant="black"
                    className={styles.closeButton}
                    label="Close"
                    onClick={closeHandler}
                />
            </div>
        </div>
    );

    return (
        <SidePanel
            side="right"
            hideBackdrop
            disableEnforceFocus
            zIndex={props.zIndex ?? 1000}
            width={RECURRING_PANEL_WIDTH}
            classesInner={{
                root: styles.root,
                paper: clsx(styles.paper, classes.paper, { [styles.paperLeaving]: isLeaving })
            }}
            classesBackdrop={{ classes: { root: styles.backdrop } }}
            onClose={closeHandler}
        >
            {content}
        </SidePanel>
    );
};

export default RecurringSeriesViewPanel;
